import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Icon from "../components/Icon";
import CarCard from "../components/CarCard";
import CarGridState from "../components/CarGridState";
import ProcessSteps from "../components/ProcessSteps";
import Faq from "../components/Faq";
import CountUp from "../components/CountUp";
import { PROMISES, SERVICES, STATS } from "../content/site";
import { carStatus, formatPrice, PRICE_CAPS } from "../lib/format";
import useDocumentTitle from "../lib/useDocumentTitle";
import s from "./Home.module.css";

/** Hero search: brand + budget → stock search, or straight into the import wizard. */
function QuickSearch({ cars, importBrands }) {
  const navigate = useNavigate();
  const [brand, setBrand] = useState("");
  const [max, setMax] = useState("");
  const brands = [...new Set([...cars.map((c) => c.brand), ...importBrands])].sort((a, b) => a.localeCompare(b, "cs"));

  const searchStock = (e) => {
    e.preventDefault();
    const q = new URLSearchParams();
    if (brand) q.set("znacka", brand);
    if (max) q.set("max", max);
    navigate(`/nabidka${q.toString() ? `?${q}` : ""}`);
  };

  return (
    <form className={s.search} onSubmit={searchStock} aria-labelledby="qs-title">
      <h2 id="qs-title" className={s.searchTitle}>Najděte svůj elektromobil</h2>
      <div className={s.searchRow}>
        <label className={s.searchField}>
          <span>Značka</span>
          <select value={brand} onChange={(e) => setBrand(e.target.value)}>
            <option value="">Libovolná</option>
            {brands.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>
        <label className={s.searchField}>
          <span>Rozpočet do</span>
          <select value={max} onChange={(e) => setMax(e.target.value)}>
            <option value="">Bez limitu</option>
            {PRICE_CAPS.map((p) => <option key={p} value={p}>{formatPrice(p)}</option>)}
          </select>
        </label>
      </div>
      <button type="submit" className="btn btn-primary btn-lg btn-block">
        Hledat ve skladu <Icon name="search" size={18} />
      </button>
      <button type="button" className="btn btn-ghost btn-block" onClick={() => navigate("/dovoz", { state: { brand } })}>
        Poptat dovoz na míru <Icon name="arrow-right" size={18} />
      </button>
    </form>
  );
}

export default function Home({ cars, carsStatus, wizardConfig }) {
  useDocumentTitle(null);
  const available = cars.filter((c) => carStatus(c) !== "sold");
  // Featured first, topped up with other cars on offer so the row is full.
  const shown = [...available.filter((c) => c.featured), ...available.filter((c) => !c.featured)].slice(0, 4);

  return (
    <>
      <section className={s.hero}>
        <div className={s.heroMedia} aria-hidden="true">
          {/* Swap the photo by replacing public/hero.jpg (16:9, car roughly centred). */}
          <img src="/hero.jpg" alt="" width="1536" height="864" fetchpriority="high" />
        </div>
        <div className={`container ${s.heroText}`}>
          <p className="eyebrow">Dovoz elektromobilů z EU na míru</p>
          <h1 className={s.title}>Jezděte <span>elektricky.</span></h1>
          <p className={s.lead}>
            Najdeme, prověříme a dovezeme elektromobil přesně podle vašich představ, nebo si vyberte ze skladových vozů.
          </p>
        </div>
        <div className={`container ${s.heroBottom}`}>
          <QuickSearch cars={cars} importBrands={wizardConfig.brands || []} />
          <ul className={s.promises}>
            {PROMISES.map((p) => (
              <li key={p.text}><Icon name={p.icon} size={26} />{p.text}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section" aria-labelledby="stock-title">
        <div className="container">
          <div className={s.sectionHead}>
            <div>
              <p className="eyebrow">Skladem v ČR</p>
              <h2 id="stock-title" className="h2">Vozy připravené k předání</h2>
            </div>
            {cars.length > 0 && (
              <Link to="/nabidka" className="link-arrow">
                Celá nabídka ({cars.length}) <Icon name="arrow-right" size={16} />
              </Link>
            )}
          </div>
          <CarGridState status={carsStatus} count={shown.length}>
            <div className={s.cars}>
              {shown.map((c) => <CarCard key={c.id} car={c} />)}
            </div>
          </CarGridState>
        </div>
      </section>

      <section className="section" aria-labelledby="process-title">
        <div className="container">
          <div className={s.sectionHead}>
            <div>
              <p className="eyebrow">Dovoz na míru</p>
              <h2 id="process-title" className="h2">Od poptávky ke klíčům za 2–4 týdny</h2>
            </div>
            <Link to="/dovoz" className="link-arrow">Začít poptávku <Icon name="arrow-right" size={16} /></Link>
          </div>
          <ProcessSteps />
        </div>
      </section>

      <section className="section" aria-labelledby="services-title">
        <div className={`container ${s.why}`}>
          <div className={s.whyIntro}>
            <p className="eyebrow">Proč VoltNaPřání</p>
            <h2 id="services-title" className="h2">Vše pro bezpečný dovoz v jedné službě</h2>
            <ul className={s.checklist}>
              {["Žádné skryté poplatky", "Ověřený stav baterie u každého vozu", "STK a registrace v ceně", "Auto předáme vyčištěné a připravené"].map((t) => (
                <li key={t}><Icon name="check" size={18} strokeWidth={2.25} />{t}</li>
              ))}
            </ul>
          </div>
          <div className={s.services}>
            {SERVICES.map((sv) => (
              <article key={sv.title} className={s.service}>
                <Icon name={sv.icon} size={24} className={s.serviceIcon} />
                <h3>{sv.title}</h3>
                <p>{sv.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Faq />

      <section className={s.band}>
        <div className={`container ${s.bandInner}`}>
          <div className={s.bandText}>
            <h2 className="h2">Hledáte konkrétní model?</h2>
            <p>Pošlete nám parametry a do 24 hodin se ozveme s nabídkou vozů z EU.</p>
          </div>
          <Link to="/dovoz" className="btn btn-primary btn-lg">
            Poptat dovoz <Icon name="arrow-right" size={18} />
          </Link>
        </div>
        <div className="container">
          <dl className={s.stats}>
            {STATS.map((st) => (
              <div key={st.label}>
                <Icon name={st.icon} size={26} />
                <dd className="num"><CountUp value={st.value} /></dd>
                <dt>{st.label}</dt>
              </div>
            ))}
          </dl>
        </div>
      </section>
    </>
  );
}
